import { UseQueryResult } from '@tanstack/react-query';
import { useRef } from 'react';

/**
 * Utility hook to minimize the impact of non-actionable changes to results
 * from `useQueries`. It checks for reference equality on the top-level properties
 * of each result object, and only returns an updated result when there is a
 * change.
 *
 * @param results - Results from a call to `useQueries`.
 */
// TODO: see if this utility is still needed whenever we are able to upgrade
// to react-query v5 (dependent on updating to react 18). See
// https://github.com/TanStack/query/issues/5137
export function useMemoQueriesResults<T>(results: Array<UseQueryResult<T>>): Array<UseQueryResult<T>> {
  const ref = useRef<Array<UseQueryResult<T>>>();
  if (ref.current === undefined) {
    // First time running.
    ref.current = results;
  } else if (!compareQueryResults(ref.current, results)) {
    // Results have changed. Update the return value
    ref.current = results;
  }

  return ref.current;
}

/**
 * Compares two arrays of query results from `useQueries` using reference equality
 * on the top-level properties in each result object.
 *
 * @returns true if the results are considered the same. Otherwise, false.
 */
function compareQueryResults(prevResult: UseQueryResult[], nextResult: UseQueryResult[]): boolean {
  if (prevResult === nextResult) {
    // The results have reference quality, so they're the same.
    return true;
  }
  if (prevResult.length !== nextResult.length) {
    // The results have different lengths, so they're not the same.
    return false;
  }

  for (let i = 0; i < prevResult.length; i++) {
    if (!compareQueryResult(prevResult[i], nextResult[i])) {
      // One of the results does not match, so they're not the same.
      return false;
    }
  }

  // If all the other checks passed, consider the results the same.
  return true;
}

/**
 * Compares two query results from `useQueries` using reference equality on the
 * top-level properties in each result object.
 *
 * @returns true if the results are considered the same. Otherwise, false.
 */
function compareQueryResult(prevResult: UseQueryResult | undefined, nextResult: UseQueryResult | undefined): boolean {
  if (prevResult === undefined || nextResult === undefined) {
    // If either of the results are undefined, something probably changed. This
    // mostly exists as a type guard. It should be hard to hit this edge case.
    return false;
  }

  const optionKeys = new Set(Object.keys(prevResult).concat(Object.keys(nextResult))).values();

  // Check the top-level object properties for reference equality. We do not do a
  // deep equality check because it is expensive and should not be necessary.
  for (const key of optionKeys) {
    if (prevResult[key as keyof typeof prevResult] !== nextResult[key as keyof typeof nextResult]) {
      return false;
    }
  }

  // No differences found, consider the results the same.
  return true;
}
